import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import logoZiyara from '../data/ziyara.png';
import {
  Globe2,
  Lock,
  Plane,
  CalendarCheck2,
  Calendar,
  ClipboardList,
  Clock,
  MapPin,
  Sparkles,
  Zap,
  Check,
  Gift,
  CheckCircle2,
  ArrowRight,
  Loader2,
} from 'lucide-react';
/** Exemple de domaine personnalisé en .com (sans sous-domaine), comme un nom de domaine dédié à l’agence. */
const DEFAULT_SITE_PREVIEW_HOSTNAME = 'nomagence.com';
const DEFAULT_SITE_PREVIEW_AGENCY = 'Nom Agence';

const HERO_PREVIEW_IMAGE =
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80';

/** Vignettes offres (Unsplash, léger recadrage pour la perf). */
const previewOfferImage = (photoId: string) =>
  `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=400&q=80`;

/** Mêmes intitulés de séjours partout (aperçu site, présentation séjours, réservations). */
const PREVIEW_SEJOUR_NAMES = {
  omra: { fr: 'Omra Ramadan', en: 'Ramadan Umrah', ar: 'عمرة رمضان' },
  istanbul: { fr: 'Istanbul découverte', en: 'Istanbul Discovery', ar: 'إسطنبول - اكتشاف' },
  dubai: { fr: 'Dubaï famille', en: 'Dubai Family', ar: 'دبي للعائلة' },
} as const;

/** Miniatures d’offres pour l’aperçu site (section « Nos séjours », même esprit que la page agence). */
const SITE_PREVIEW_OFFERS = [
  {
    title: PREVIEW_SEJOUR_NAMES.omra,
    price: { fr: 'À partir de 89 000 DA', en: 'From 89,000 DZD', ar: 'ابتداءً من 89,000 دج' },
    image: previewOfferImage('photo-1565019001609-0e34a6a22189'),
  },
  {
    title: PREVIEW_SEJOUR_NAMES.istanbul,
    price: { fr: 'À partir de 125 000 DA', en: 'From 125,000 DZD', ar: 'ابتداءً من 125,000 دج' },
    image: previewOfferImage('photo-1527838832700-5059252407fa'),
  },
  {
    title: PREVIEW_SEJOUR_NAMES.dubai,
    price: { fr: 'À partir de 195 000 DA', en: 'From 195,000 DZD', ar: 'ابتداءً من 195,000 دج' },
    image: previewOfferImage('photo-1512453979798-5ea266f8880c'),
  },
] as const;

const SEJOURS_PREVIEW_CARD_IMAGES = {
  omra: previewOfferImage('photo-1565019001609-0e34a6a22189'),
  istanbul: previewOfferImage('photo-1527838832700-5059252407fa'),
  dubai: previewOfferImage('photo-1512453979798-5ea266f8880c'),
} as const;

/** Hauteur commune des maquettes (pas de scroll dans les aperçus). */
const FEATURE_VISUAL_HEIGHT = 'h-[20rem]';

type WaitlistLocale = 'fr' | 'en' | 'ar';

type WaitlistLocalized = { fr: string; en: string; ar: string };

const WAITLIST_COPY: Record<
  WaitlistLocale,
  {
    badge: string;
    heroTitle: string;
    heroSubtitleBeforeBold: string;
    heroSubtitleBold: string;
    heroSubtitleAfterBold: string;
    pillarsTitle: string;
    offerBannerBeforeBold: string;
    offerBannerBold: string;
    offerBannerAfterBold: string;
    successTitle: string;
    successBodyBeforeBold: string;
    successBodyBold: string;
    successBodyAfterBold: string;
    backHome: string;
    formTitle: string;
    formIntro: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    agencyName: string;
    agencyNamePlaceholder: string;
    rc: string;
    rcPlaceholder: string;
    rcHint: string;
    wilaya: string;
    wilayaPlaceholder: string;
    phone: string;
    message: string;
    messagePlaceholder: string;
    acceptContactBeforeBold: string;
    acceptContactBold: string;
    acceptContactAfterBold: string;
    sending: string;
    submit: string;
    privacy: string;
  }
> = {
  fr: {
    badge: 'Lancement : 3 mois offerts pour amelioreeer ton image et ton activité',
    heroTitle: 'Une plateforme pensée pour les agences',
    heroSubtitleBeforeBold:
      'Notre plateforme vous aide à attirer des voyageurs, renforcer votre image et piloter vos dossiers et paiements depuis un seul espace. Inscrivez-vous sur la liste d’attente : priorité à l’ouverture et ',
    heroSubtitleBold: '3 mois gratuits',
    heroSubtitleAfterBold: ' pour tester l’impact sur votre activité.',
    pillarsTitle: 'Trois piliers pour attirer des clients, vendre vos séjours et garder la main sur l’activité.',
    offerBannerBeforeBold: 'Les agences inscrites en priorité pourront tester ',
    offerBannerBold: '3 mois sans abonnement',
    offerBannerAfterBold:
      ' sur l’offre choisie à l’ouverture : idéal pour évaluer le retour sur vos ventes et votre organisation avant de vous engager.',
    successTitle: 'Merci — vous êtes sur la liste prioritaire',
    successBodyBeforeBold:
      'Nous vous écrirons à l’adresse indiquée pour vous confirmer la suite (accès, calendrier et conditions des ',
    successBodyBold: '3 mois offerts',
    successBodyAfterBold: ') dès que l’ouverture aux agences sera actée.',
    backHome: 'Retour à l’accueil',
    formTitle: 'Réservez votre place prioritaire',
    formIntro:
      'Recevez les informations sur l’ouverture et les conditions des 3 mois offerts. Aucun paiement n’est demandé aujourd’hui : vous validez d’abord l’intérêt pour votre agence.',
    firstName: 'Prénom',
    firstNamePlaceholder: 'Votre prénom',
    lastName: 'Nom',
    lastNamePlaceholder: 'Votre nom',
    email: 'E-mail',
    emailPlaceholder: 'vous@exemple.dz',
    agencyName: 'Nom de l’agence',
    agencyNamePlaceholder: 'Ex. Agence Horizon Voyages',
    rc: 'N° registre de commerce (RC)',
    rcPlaceholder: 'Ex. 16 / 00 1234567 B 12',
    rcHint: 'Tel qu’indiqué sur votre extrait RCCM.',
    wilaya: 'Wilaya',
    wilayaPlaceholder: 'Sélectionnez une wilaya',
    phone: 'Téléphone',
    message: 'Contexte ou attentes (optionnel)',
    messagePlaceholder: 'Taille de l’équipe, objectifs de développement, besoins précis…',
    acceptContactBeforeBold:
      'J’accepte d’être recontacté(e) par Ziyara pour la suite de la liste d’attente et la présentation de l’offre (dont les ',
    acceptContactBold: '3 mois offerts',
    acceptContactAfterBold: ').',
    sending: 'Envoi en cours…',
    submit: 'Valider mon inscription prioritaire',
    privacy:
      'Vos données servent uniquement à vous recontacter sur ce dossier ; elles ne sont pas revendues. Conformément à notre politique de confidentialité.',
  },
  en: {
    badge: 'Launch: 3 months free to improve your brand and your activity',
    heroTitle: 'A platform built for travel agencies',
    heroSubtitleBeforeBold:
      'Our platform helps you attract travelers, strengthen your brand, and manage files and payments in one place. Join the waitlist: get early access and ',
    heroSubtitleBold: '3 free months',
    heroSubtitleAfterBold: ' to measure the impact on your business.',
    pillarsTitle: 'Three pillars to attract clients, sell your packages, and stay in control.',
    offerBannerBeforeBold: 'Priority agencies will be able to try ',
    offerBannerBold: '3 months with no subscription',
    offerBannerAfterBold:
      ' on the plan you choose at launch—ideal to evaluate the impact on sales and operations before committing.',
    successTitle: "Thank you — you're on the priority list",
    successBodyBeforeBold:
      "We'll email you at the address provided with next steps (access, timeline, and terms of the ",
    successBodyBold: '3 free months',
    successBodyAfterBold: ') as soon as agency onboarding opens.',
    backHome: 'Back to home',
    formTitle: 'Reserve your priority spot',
    formIntro:
      'Get updates about launch and the 3 free months. No payment is required today: you simply confirm your interest for your agency.',
    firstName: 'First name',
    firstNamePlaceholder: 'Your first name',
    lastName: 'Last name',
    lastNamePlaceholder: 'Your last name',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    agencyName: 'Agency name',
    agencyNamePlaceholder: 'e.g. Horizon Travel Agency',
    rc: 'Commercial register number (RC)',
    rcPlaceholder: 'e.g. 16 / 00 1234567 B 12',
    rcHint: 'As shown on your RCCM extract.',
    wilaya: 'Wilaya',
    wilayaPlaceholder: 'Select a wilaya',
    phone: 'Phone',
    message: 'Context or expectations (optional)',
    messagePlaceholder: 'Team size, growth goals, specific needs…',
    acceptContactBeforeBold:
      'I agree to be contacted by Ziyara about the waitlist and the offer details (including the ',
    acceptContactBold: '3 free months',
    acceptContactAfterBold: ').',
    sending: 'Sending…',
    submit: 'Submit my priority request',
    privacy:
      'Your data is only used to contact you about this request; it is not sold. In accordance with our privacy policy.',
  },
  ar: {
    badge: 'الإطلاق: 3 أشهر مجانًا لتحسين صورتك ونشاطك',
    heroTitle: 'منصة مُصمَّمة لوكالات السفر',
    heroSubtitleBeforeBold:
      'تساعدك منصتنا على جذب المسافرين، تعزيز صورتك، وإدارة الملفات والمدفوعات من مكان واحد. سجّل في قائمة الانتظار: أولوية عند الإطلاق و',
    heroSubtitleBold: '3 أشهر مجانًا',
    heroSubtitleAfterBold: ' لقياس الأثر على نشاطك.',
    pillarsTitle: 'ثلاث ركائز لجذب العملاء، بيع العروض، والبقاء على السيطرة.',
    offerBannerBeforeBold: 'ستتمكن الوكالات ذات الأولوية من تجربة ',
    offerBannerBold: '3 أشهر بدون اشتراك',
    offerBannerAfterBold:
      ' على الخطة المختارة عند الإطلاق—مثالي لتقييم الأثر على المبيعات والتنظيم قبل الالتزام.',
    successTitle: 'شكرًا — تم تسجيلك في القائمة ذات الأولوية',
    successBodyBeforeBold:
      'سنراسلك على البريد الذي أدخلته لتأكيد الخطوات القادمة (الوصول، الجدول الزمني، وشروط ',
    successBodyBold: '3 أشهر المجانية',
    successBodyAfterBold: ') فور فتح التسجيل للوكالات.',
    backHome: 'العودة إلى الرئيسية',
    formTitle: 'احجز مكانك في الأولوية',
    formIntro:
      'استلم معلومات الإطلاق وشروط 3 أشهر المجانية. لا يوجد دفع اليوم: أنت تؤكد فقط اهتمامك لوكالتك.',
    firstName: 'الاسم الأول',
    firstNamePlaceholder: 'اسمك الأول',
    lastName: 'اللقب',
    lastNamePlaceholder: 'لقبك',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'you@example.com',
    agencyName: 'اسم الوكالة',
    agencyNamePlaceholder: 'مثال: وكالة Horizon للسفر',
    rc: 'رقم السجل التجاري (RC)',
    rcPlaceholder: 'مثال: 16 / 00 1234567 B 12',
    rcHint: 'كما هو مذكور في مستخرج RCCM.',
    wilaya: 'الولاية',
    wilayaPlaceholder: 'اختر ولاية',
    phone: 'الهاتف',
    message: 'سياق أو توقعات (اختياري)',
    messagePlaceholder: 'حجم الفريق، أهداف النمو، احتياجات محددة…',
    acceptContactBeforeBold:
      'أوافق على أن يتم التواصل معي من طرف Ziyara بخصوص قائمة الانتظار وتفاصيل العرض (بما في ذلك ',
    acceptContactBold: '3 أشهر المجانية',
    acceptContactAfterBold: ').',
    sending: 'جارٍ الإرسال…',
    submit: 'تأكيد طلبي ذي الأولوية',
    privacy:
      'تُستخدم بياناتك فقط للتواصل معك بخصوص هذا الطلب ولا يتم بيعها، وفقًا لسياسة الخصوصية.',
  },
};

function t(locale: WaitlistLocale, value: WaitlistLocalized): string {
  return value[locale] ?? value.fr;
}

const VISUAL_COPY: Record<
  WaitlistLocale,
  {
    siteHeroPrefix: string;
    siteHeroSubtitle: string;
    siteNavTrips: string;
    siteNavDestinations: string;
    siteNavAbout: string;
    siteQuote: string;
    siteSectionTitle: string;
    siteSectionSubtitle: string;
    reservationsTitle: string;
    activeCount: string;
    reservationListLabel: string;
    reservationRowPrefix: string;
    statusConfirmed: string;
    statusPending: string;
    statusPaid: string;
    statusUnpaid: string;
    quickCreation: string;
    fewMinutes: string;
    stepInfos: string;
    stepPlans: string;
    stepPublish: string;
    presentation: string;
    clientRender: string;
  }
> = {
  fr: {
    siteHeroPrefix: 'Bienvenue chez',
    siteHeroSubtitle: 'Des voyages inoubliables et des expériences uniques vous attendent.',
    siteNavTrips: 'Nos séjours',
    siteNavDestinations: 'Destinations',
    siteNavAbout: 'À propos',
    siteQuote: 'Devis',
    siteSectionTitle: 'Nos séjours',
    siteSectionSubtitle: 'Découvrez nos séjours pour des expériences inoubliables.',
    reservationsTitle: 'Réservations',
    activeCount: '12 actives',
    reservationListLabel: 'Liste des réservations',
    reservationRowPrefix: 'Réservation',
    statusConfirmed: 'Confirmé',
    statusPending: 'En attente',
    statusPaid: 'Payé',
    statusUnpaid: 'Non payé',
    quickCreation: 'Création rapide',
    fewMinutes: 'Quelques minutes',
    stepInfos: 'Infos & médias',
    stepPlans: 'Formules & prix',
    stepPublish: 'Publication',
    presentation: 'Présentation',
    clientRender: '— rendu client',
  },
  en: {
    siteHeroPrefix: 'Welcome to',
    siteHeroSubtitle: 'Unforgettable trips and unique experiences await you.',
    siteNavTrips: 'Trips',
    siteNavDestinations: 'Destinations',
    siteNavAbout: 'About',
    siteQuote: 'Quote',
    siteSectionTitle: 'Our trips',
    siteSectionSubtitle: 'Discover our trips for unforgettable experiences.',
    reservationsTitle: 'Bookings',
    activeCount: '12 active',
    reservationListLabel: 'Booking list',
    reservationRowPrefix: 'Booking',
    statusConfirmed: 'Confirmed',
    statusPending: 'Pending',
    statusPaid: 'Paid',
    statusUnpaid: 'Unpaid',
    quickCreation: 'Quick setup',
    fewMinutes: 'Few minutes',
    stepInfos: 'Info & media',
    stepPlans: 'Plans & pricing',
    stepPublish: 'Publish',
    presentation: 'Showcase',
    clientRender: '— client view',
  },
  ar: {
    siteHeroPrefix: 'مرحبًا بكم في',
    siteHeroSubtitle: 'رحلات لا تُنسى وتجارب مميزة بانتظاركم.',
    siteNavTrips: 'العروض',
    siteNavDestinations: 'الوجهات',
    siteNavAbout: 'من نحن',
    siteQuote: 'عرض سعر',
    siteSectionTitle: 'عروضنا',
    siteSectionSubtitle: 'اكتشفوا عروضنا لتجارب سفر لا تُنسى.',
    reservationsTitle: 'الحجوزات',
    activeCount: '12 نشطة',
    reservationListLabel: 'قائمة الحجوزات',
    reservationRowPrefix: 'حجز',
    statusConfirmed: 'مؤكد',
    statusPending: 'قيد الانتظار',
    statusPaid: 'مدفوع',
    statusUnpaid: 'غير مدفوع',
    quickCreation: 'إنشاء سريع',
    fewMinutes: 'بضع دقائق',
    stepInfos: 'المعلومات والوسائط',
    stepPlans: 'الصيغ والأسعار',
    stepPublish: 'النشر',
    presentation: 'عرض الواجهة',
    clientRender: '— عرض العميل',
  },
};

/** Cartes « présentation » : infos type fiche séjour (comme sur les cartes catalogue). */

const SEJOURS_PRESENTATION_CARDS = [
  {
    title: PREVIEW_SEJOUR_NAMES.omra,
    imageKey: 'omra' as const,
    duration: { fr: '12 j · 11 n', en: '12 d · 11 n', ar: '12 يوم · 11 ليلة' },
    circuit: 'Makkah & Médine',
    departure: { fr: 'Départ 15 mars 2026', en: 'Departure Mar 15, 2026', ar: 'الانطلاق 15 مارس 2026' },
    formulas: { fr: '3 formules', en: '3 plans', ar: '3 صيغ' },
    price: { fr: 'Dès 95 000 DA', en: 'From 95,000 DZD', ar: 'ابتداءً من 95,000 دج' },
    rating: 4,
    dotClass: 'bg-amber-400',
  },
  {
    title: PREVIEW_SEJOUR_NAMES.istanbul,
    imageKey: 'istanbul' as const,
    duration: { fr: '6 j · 5 n', en: '6 d · 5 n', ar: '6 أيام · 5 ليالٍ' },
    circuit: 'Istanbul · Sultanahmet & Bosphore',
    departure: { fr: 'Départ 22 avr. 2026', en: 'Departure Apr 22, 2026', ar: 'الانطلاق 22 أفريل 2026' },
    formulas: { fr: '2 formules', en: '2 plans', ar: '2 صيغتان' },
    price: { fr: 'Dès 125 000 DA', en: 'From 125,000 DZD', ar: 'ابتداءً من 125,000 دج' },
    rating: 5,
    dotClass: 'bg-sky-400',
  },
] as const;

/** Ordre des cartes : 1. site — 2. séjours — 3. réservations (ne pas permuter sans mettre à jour ce tableau). */
const WAITLIST_FEATURE_ORDER = ['site', 'sejours', 'reservations'] as const;

const features = [
  {
    id: 'site' as const,
    icon: Globe2,
    title: { fr: 'Votre vitrine en ligne', en: 'Your online storefront', ar: 'واجهة وكالتك على الإنترنت' } as const,
    description: {
      fr: 'Une image professionnelle qui rassure les voyageurs et les pousse à vous contacter.',
      en: 'A professional image that builds trust and drives inquiries.',
      ar: 'صورة احترافية تُطمئن المسافرين وتزيد طلبات التواصل.',
    } as const,
    accent: 'from-sky-500 to-blue-600',
    /** Domaine public d’exemple (racine .com). */
    agencySiteHostname: DEFAULT_SITE_PREVIEW_HOSTNAME,
    agencySiteName: DEFAULT_SITE_PREVIEW_AGENCY,
  },
  {
    id: 'reservations' as const,
    icon: CalendarCheck2,
    title: { fr: 'Réservations bien suivies', en: 'Bookings under control', ar: 'حجوزات تحت السيطرة' } as const,
    description: {
      fr: 'Dossiers, attentes et paiements au même endroit : moins d’erreurs.',
      en: 'Files, follow-ups, and payments in one place—fewer mistakes.',
      ar: 'الملفات والمتابعة والمدفوعات في مكان واحد—أخطاء أقل.',
    } as const,
    accent: 'from-violet-500 to-indigo-600',
  },
  {
    id: 'sejours' as const,
    icon: Sparkles,
    title: { fr: 'Offres qui se vendent', en: 'Offers that convert', ar: 'عروض تُباع' } as const,
    description: {
      fr: 'Mettez vos séjours en avant vite et clairement pour susciter l’achat.',
      en: 'Showcase your packages quickly and clearly to drive bookings.',
      ar: 'اعرض باقاتك بسرعة وبوضوح لزيادة الحجوزات.',
    } as const,
    accent: 'from-amber-500 to-orange-600',
  },
];

function featuresInDisplayOrder(): typeof features {
  return WAITLIST_FEATURE_ORDER.map((id) => features.find((f) => f.id === id)!) as typeof features;
}

/**
 * Aperçu aligné sur la page agence réelle (`frontend-agency` / `AgencyPage`) :
 * URL, header, hero, puis section « Nos séjours » avec grille d’offres.
 */
function FeatureVisualSite({
  hostname,
  agencyName,
  locale,
}: {
  hostname: string;
  agencyName: string;
  locale: WaitlistLocale;
}) {
  const visual = VISUAL_COPY[locale];
  const displayUrl = `https://${hostname}`;
  const heroTitle = `${visual.siteHeroPrefix} ${agencyName}`;
  const heroSubtitle = visual.siteHeroSubtitle;

  return (
    <div className={`relative ${FEATURE_VISUAL_HEIGHT} overflow-hidden bg-slate-950`} aria-hidden>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_15%,rgba(56,189,248,0.12),transparent_55%)]" />
      <div className="relative flex h-full flex-col p-2">
        {/* Chrome navigateur */}
        <div className="flex shrink-0 items-center gap-2 rounded-t-lg border border-white/10 border-b-0 bg-[#2a2a2e] px-2 py-1.5">
          <div className="flex gap-1">
            <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
            <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
            <span className="h-2 w-2 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md bg-black/35 px-2 py-1">
            <Lock className="h-2.5 w-2.5 shrink-0 text-emerald-400/90" aria-hidden strokeWidth={2.5} />
            <span className="truncate font-mono text-[9px] text-slate-300" title={displayUrl}>
              {displayUrl}
            </span>
          </div>
        </div>

        {/* Site agence : Header + Hero + liste d’offres */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-lg border border-t-0 border-white/10 bg-slate-900 shadow-inner">
          <header className="flex shrink-0 items-center justify-between gap-1 border-b border-white/10 bg-slate-800 px-1.5 py-1">
            <div className="flex min-w-0 items-center gap-1">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10">
                <Plane className="h-3 w-3 text-white" />
              </span>
              <span className="hidden min-w-0 truncate text-[7px] font-semibold text-white/95 sm:inline">
                {agencyName}
              </span>
            </div>
            <nav className="hidden flex-1 justify-center gap-2 px-1 sm:flex">
              <span className="text-[6.5px] text-white/75">{visual.siteNavTrips}</span>
              <span className="text-[6.5px] text-white/75">{visual.siteNavDestinations}</span>
              <span className="text-[6.5px] text-white/75">{visual.siteNavAbout}</span>
            </nav>
            <button
              type="button"
              tabIndex={-1}
              className="shrink-0 rounded-full bg-indigo-600 px-2 py-0.5 text-[6.5px] font-semibold text-white shadow-sm"
            >
              {visual.siteQuote}
            </button>
          </header>

          <div className="relative h-[5.25rem] shrink-0">
            <img
              src={HERO_PREVIEW_IMAGE}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative z-10 flex h-full flex-col justify-center px-2 py-1 text-white">
              <p className="text-[9px] font-bold leading-tight tracking-tight drop-shadow-sm line-clamp-2">
                {heroTitle}
              </p>
              <p className="mt-0.5 text-[6.5px] leading-snug text-white/90 line-clamp-2 drop-shadow-sm">
                {heroSubtitle}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[6px] font-semibold shadow-sm">
                  {visual.siteNavTrips}
                </span>
                <span className="rounded-full bg-white px-1.5 py-0.5 text-[6px] font-semibold text-slate-900 shadow-sm">
                  {visual.siteNavAbout}
                </span>
              </div>
            </div>
          </div>

          {/* Section offres / séjours (fond clair, titre, grille) */}
          <section className="min-h-0 flex-1 overflow-hidden border-t border-slate-700/80 bg-slate-100">
            <div className="px-1 pb-0.5 pt-1">
              <div className="mb-0.5 text-center">
                <p className="text-[7px] font-bold text-slate-800">{visual.siteSectionTitle}</p>
                <p className="mt-px line-clamp-1 px-0.5 text-[5.5px] leading-tight text-slate-600">
                  {visual.siteSectionSubtitle}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-0.5">
                {SITE_PREVIEW_OFFERS.map((offer) => (
                  <article
                    key={offer.title}
                    className="overflow-hidden rounded-md border border-slate-200/90 bg-white shadow-sm"
                  >
                    <div className="relative h-7 w-full overflow-hidden bg-slate-200 ring-1 ring-inset ring-black/5">
                      <img
                        src={offer.image}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="space-y-px px-0.5 py-0.5">
                      <p className="line-clamp-2 text-[5.5px] font-semibold leading-tight text-slate-900">
                        {t(locale, offer.title)}
                      </p>
                      <p className="text-[5px] font-medium text-indigo-600">{t(locale, offer.price)}</p>
                      <div className="flex items-center justify-between pt-px">
                        <span className="h-0.5 w-5 rounded-full bg-slate-200" />
                        <Sparkles className="h-2 w-2 shrink-0 text-amber-500/80" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

type ReservationLinePreview = {
  name: string;
  confirmationLabel: string;
  confirmationTone: string;
  paymentLabel: string;
  paymentTone: string;
};

/** Aperçu : dossiers séjour + liste des réservations avec statuts à côté de chaque ligne. */
function FeatureVisualReservations({ locale }: { locale: WaitlistLocale }) {
  const visual = VISUAL_COPY[locale];
  const rows: { label: string; reservationsList: ReservationLinePreview[] }[] = [
    {
      label: t(locale, PREVIEW_SEJOUR_NAMES.omra),
      reservationsList: [
        {
          name: `${visual.reservationRowPrefix} 1`,
          confirmationLabel: visual.statusConfirmed,
          confirmationTone: 'bg-emerald-500/25 text-emerald-200',
          paymentLabel: visual.statusPaid,
          paymentTone: 'bg-sky-500/25 text-sky-200',
        },
        {
          name: `${visual.reservationRowPrefix} 2`,
          confirmationLabel: visual.statusConfirmed,
          confirmationTone: 'bg-emerald-500/25 text-emerald-200',
          paymentLabel: visual.statusUnpaid,
          paymentTone: 'bg-rose-500/25 text-rose-200',
        },
        {
          name: `${visual.reservationRowPrefix} 3`,
          confirmationLabel: visual.statusPending,
          confirmationTone: 'bg-amber-500/25 text-amber-200',
          paymentLabel: visual.statusUnpaid,
          paymentTone: 'bg-rose-500/25 text-rose-200',
        },
      ],
    },
    {
      label: t(locale, PREVIEW_SEJOUR_NAMES.istanbul),
      reservationsList: [
        {
          name: `${visual.reservationRowPrefix} 1`,
          confirmationLabel: visual.statusPending,
          confirmationTone: 'bg-amber-500/25 text-amber-200',
          paymentLabel: visual.statusUnpaid,
          paymentTone: 'bg-rose-500/25 text-rose-200',
        },
        {
          name: `${visual.reservationRowPrefix} 2`,
          confirmationLabel: visual.statusConfirmed,
          confirmationTone: 'bg-emerald-500/25 text-emerald-200',
          paymentLabel: visual.statusPaid,
          paymentTone: 'bg-sky-500/25 text-sky-200',
        },
      ],
    },
    {
      label: t(locale, PREVIEW_SEJOUR_NAMES.dubai),
      reservationsList: [
        {
          name: `${visual.reservationRowPrefix} 1`,
          confirmationLabel: visual.statusConfirmed,
          confirmationTone: 'bg-emerald-500/25 text-emerald-200',
          paymentLabel: visual.statusPaid,
          paymentTone: 'bg-sky-500/25 text-sky-200',
        },
        {
          name: `${visual.reservationRowPrefix} 2`,
          confirmationLabel: visual.statusPending,
          confirmationTone: 'bg-amber-500/25 text-amber-200',
          paymentLabel: visual.statusUnpaid,
          paymentTone: 'bg-rose-500/25 text-rose-200',
        },
        {
          name: `${visual.reservationRowPrefix} 3`,
          confirmationLabel: visual.statusConfirmed,
          confirmationTone: 'bg-emerald-500/25 text-emerald-200',
          paymentLabel: visual.statusUnpaid,
          paymentTone: 'bg-rose-500/25 text-rose-200',
        },
      ],
    },
  ];

  return (
    <div
      className={`relative ${FEATURE_VISUAL_HEIGHT} overflow-hidden bg-gradient-to-br from-indigo-950 via-violet-950 to-slate-900`}
      aria-hidden
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_30%,rgba(139,92,246,0.2),transparent_55%)]" />
      <div className="relative flex h-full flex-col gap-1 overflow-hidden p-1.5 pt-2">
        <div className="flex shrink-0 items-center justify-between rounded px-1 py-0.5">
          <span className="text-[8px] font-semibold uppercase tracking-wider text-violet-300/95">{visual.reservationsTitle}</span>
          <span className="rounded bg-white/10 px-1 py-px text-[7px] text-slate-300">{visual.activeCount}</span>
        </div>
        {rows.map((row) => (
          <div
            key={row.label}
            className="shrink-0 rounded-md border border-white/10 bg-black/35 px-1.5 py-1 shadow-sm backdrop-blur-sm"
          >
            <div className="flex items-start gap-1">
              <CalendarCheck2 className="mt-px h-4 w-4 shrink-0 text-violet-400/75" />
              <div className="min-w-0 flex-1">
                <div className="min-w-0">
                  <div className="truncate text-[9px] font-semibold leading-tight text-white">{row.label}</div>
                  <div className="mt-px h-px w-10 rounded-full bg-white/15" />
                </div>
                <div className="mt-1 border-t border-white/10 pt-1">
                  <div className="mb-0.5 flex items-center gap-0.5 text-[5.5px] font-semibold uppercase tracking-wide text-violet-200/90">
                    <ClipboardList className="h-2 w-2 shrink-0" strokeWidth={2.5} />
                    <span className="leading-none">{visual.reservationListLabel}</span>
                  </div>
                  <ul className="list-none space-y-px">
                    {row.reservationsList.map((item) => (
                      <li
                        key={`${row.label}-${item.name}`}
                        className="flex items-center justify-between gap-0.5 rounded border border-white/10 bg-white/5 px-0.5 py-px"
                      >
                        <span className="min-w-0 truncate text-[6px] font-medium leading-tight text-white/90">
                          {item.name}
                        </span>
                        <div className="flex shrink-0 items-center gap-px">
                          <span
                            className={`whitespace-nowrap rounded px-0.5 py-px text-[5px] font-semibold leading-none ${item.confirmationTone}`}
                          >
                            {item.confirmationLabel}
                          </span>
                          <span
                            className={`whitespace-nowrap rounded px-0.5 py-px text-[5px] font-semibold leading-none ${item.paymentTone}`}
                          >
                            {item.paymentLabel}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Aperçu : création rapide (parcours / progression) + présentation vitrine (cartes séjours). */
function FeatureVisualSejours({ locale }: { locale: WaitlistLocale }) {
  const visual = VISUAL_COPY[locale];
  const creationSteps = [visual.stepInfos, visual.stepPlans, visual.stepPublish];
  return (
    <div
      className={`relative ${FEATURE_VISUAL_HEIGHT} overflow-hidden bg-gradient-to-br from-orange-950 via-amber-950/90 to-slate-900`}
      aria-hidden
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(251,191,36,0.12),transparent_50%)]" />
      <div className="relative flex h-full flex-col gap-1.5 p-1.5">
        {/* Création rapide */}
        <div className="shrink-0 rounded-md border border-amber-400/30 bg-black/40 p-1.5 shadow-sm backdrop-blur-sm">
          <div className="mb-1 flex items-center justify-between gap-1">
            <div className="flex items-center gap-0.5">
              <Zap className="h-3 w-3 text-amber-300" />
              <span className="text-[7.5px] font-bold tracking-tight text-amber-50">{visual.quickCreation}</span>
            </div>
            <span className="rounded-full bg-emerald-500/25 px-1 py-px text-[6px] font-semibold text-emerald-200">
              {visual.fewMinutes}
            </span>
          </div>
          <div className="flex gap-0.5">
            {creationSteps.map((label) => (
              <div
                key={label}
                className="flex min-w-0 flex-1 flex-col items-center gap-px rounded border border-white/10 bg-white/5 px-0.5 py-0.5"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 text-[6px] font-bold text-white shadow-sm">
                  <Check className="h-2 w-2" strokeWidth={3} />
                </span>
                <span className="line-clamp-2 text-center text-[5px] font-medium leading-tight text-white/85">
                  {label}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-1 h-0.5 overflow-hidden rounded-full bg-white/10">
            <div className="h-full w-[92%] rounded-full bg-gradient-to-r from-amber-400 to-orange-500 shadow-sm shadow-amber-900/40" />
          </div>
        </div>

        {/* Présentation */}
        <div className="flex min-h-0 flex-1 flex-col rounded-md border border-white/10 bg-black/30 p-1.5 shadow-inner">
          <div className="mb-1 flex items-center gap-0.5">
            <Sparkles className="h-3 w-3 text-amber-200" />
            <span className="text-[7.5px] font-bold text-white">{visual.presentation}</span>
            <span className="text-[5.5px] text-white/50">{visual.clientRender}</span>
          </div>
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-1">
            {SEJOURS_PRESENTATION_CARDS.map((card) => (
              <div
                key={card.title}
                className="flex min-h-0 flex-col overflow-hidden rounded border border-white/15 bg-white shadow-sm ring-1 ring-black/20"
              >
                <div className="relative h-[26%] min-h-[1.75rem] shrink-0 overflow-hidden bg-slate-200">
                  <img
                    src={SEJOURS_PREVIEW_CARD_IMAGES[card.imageKey]}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-px p-1 pt-0.5">
                  <p className="line-clamp-2 text-[6px] font-bold leading-tight text-slate-900">{t(locale, card.title)}</p>
                  <div className="flex gap-px">
                    {Array.from({ length: card.rating }, (_, i) => (
                      <span key={i} className={`h-1 w-1 shrink-0 rounded-full ${card.dotClass}`} />
                    ))}
                  </div>
                  <div className="flex items-start gap-px text-[5px] leading-tight text-slate-600">
                    <MapPin className="mt-px h-1.5 w-1.5 shrink-0 text-slate-500" strokeWidth={2.5} />
                    <span className="line-clamp-2">{card.circuit}</span>
                  </div>
                  <div className="flex items-center gap-px text-[5px] leading-tight text-slate-600">
                    <Clock className="h-1.5 w-1.5 shrink-0 text-slate-500" strokeWidth={2.5} />
                    <span>{t(locale, card.duration)}</span>
                  </div>
                  <div className="flex items-center gap-px text-[5px] leading-tight text-slate-600">
                    <Calendar className="h-1.5 w-1.5 shrink-0 text-slate-500" strokeWidth={2.5} />
                    <span className="truncate">{t(locale, card.departure)}</span>
                  </div>
                  <span className="w-fit rounded bg-slate-100 px-0.5 py-px text-[4.5px] font-semibold text-slate-700">
                    {t(locale, card.formulas)}
                  </span>
                  <div className="mt-auto flex items-center justify-between gap-px border-t border-slate-100 pt-0.5">
                    <span className="truncate text-[5.5px] font-semibold text-indigo-600">{t(locale, card.price)}</span>
                    <Sparkles
                      className={`h-2 w-2 shrink-0 ${card.dotClass === 'bg-amber-400' ? 'text-amber-500' : 'text-sky-500'}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureVisual({
  id,
  locale,
  sitePreview,
}: {
  id: (typeof features)[number]['id'];
  locale: WaitlistLocale;
  sitePreview?: { hostname: string; agencyName: string };
}) {
  switch (id) {
    case 'site':
      return (
        <FeatureVisualSite
          hostname={sitePreview?.hostname ?? DEFAULT_SITE_PREVIEW_HOSTNAME}
          agencyName={sitePreview?.agencyName ?? DEFAULT_SITE_PREVIEW_AGENCY}
          locale={locale}
        />
      );
    case 'reservations':
      return <FeatureVisualReservations locale={locale} />;
    case 'sejours':
      return <FeatureVisualSejours locale={locale} />;
    default:
      return null;
  }
}

/** Wilayas d’Algérie (codes 01–48) — libellés usuels en français. */
const WILAYAS_DZ: { code: string; name: string }[] = [
  { code: '01', name: 'Adrar' },
  { code: '02', name: 'Chlef' },
  { code: '03', name: 'Laghouat' },
  { code: '04', name: 'Oum El Bouaghi' },
  { code: '05', name: 'Batna' },
  { code: '06', name: 'Béjaïa' },
  { code: '07', name: 'Biskra' },
  { code: '08', name: 'Béchar' },
  { code: '09', name: 'Blida' },
  { code: '10', name: 'Bouira' },
  { code: '11', name: 'Tamanrasset' },
  { code: '12', name: 'Tébessa' },
  { code: '13', name: 'Tlemcen' },
  { code: '14', name: 'Tiaret' },
  { code: '15', name: 'Tizi Ouzou' },
  { code: '16', name: 'Alger' },
  { code: '17', name: 'Djelfa' },
  { code: '18', name: 'Jijel' },
  { code: '19', name: 'Sétif' },
  { code: '20', name: 'Saïda' },
  { code: '21', name: 'Skikda' },
  { code: '22', name: 'Sidi Bel Abbès' },
  { code: '23', name: 'Annaba' },
  { code: '24', name: 'Guelma' },
  { code: '25', name: 'Constantine' },
  { code: '26', name: 'Médéa' },
  { code: '27', name: 'Mostaganem' },
  { code: '28', name: 'M’Sila' },
  { code: '29', name: 'Mascara' },
  { code: '30', name: 'Ouargla' },
  { code: '31', name: 'Oran' },
  { code: '32', name: 'El Bayadh' },
  { code: '33', name: 'Illizi' },
  { code: '34', name: 'Bordj Bou Arreridj' },
  { code: '35', name: 'Boumerdès' },
  { code: '36', name: 'El Tarf' },
  { code: '37', name: 'Tindouf' },
  { code: '38', name: 'Tissemsilt' },
  { code: '39', name: 'El Oued' },
  { code: '40', name: 'Khenchela' },
  { code: '41', name: 'Souk Ahras' },
  { code: '42', name: 'Tipaza' },
  { code: '43', name: 'Mila' },
  { code: '44', name: 'Aïn Defla' },
  { code: '45', name: 'Naâma' },
  { code: '46', name: 'Aïn Témouchent' },
  { code: '47', name: 'Ghardaïa' },
  { code: '48', name: 'Relizane' },
];

export default function WaitlistPage({ locale = 'fr' }: { locale?: WaitlistLocale }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const langFromQuery = searchParams.get('lang');
  const activeLocale: WaitlistLocale =
    locale !== 'fr'
      ? locale
      : langFromQuery === 'en' || langFromQuery === 'ar'
      ? langFromQuery
      : 'fr';
  const copy = WAITLIST_COPY[activeLocale];
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    agencyName: '',
    commercialRegister: '',
    wilaya: '',
    phone: '',
    message: '',
    acceptContact: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.email.trim()) {
      setError('Veuillez renseigner votre adresse e-mail.');
      return;
    }
    if (!form.acceptContact) {
      setError('Veuillez accepter d’être recontacté(e) pour la suite de votre inscription.');
      return;
    }

    setStatus('loading');
    // Intégration API future : POST /api/waitlist
    await new Promise((r) => setTimeout(r, 900));
    setStatus('success');
  };

  return (
    <div
      className="min-h-screen bg-slate-950 text-white"
      lang={activeLocale}
      dir={activeLocale === 'ar' ? 'rtl' : 'ltr'}
    >
      <main className="pb-16 pt-8 sm:pt-12">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-white/10">
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              background:
                'radial-gradient(ellipse 80% 60% at 50% -20%, rgb(99 102 241 / 0.45), transparent), radial-gradient(ellipse 60% 50% at 100% 50%, rgb(14 165 233 / 0.25), transparent)',
            }}
          />
          <div className="relative mx-auto max-w-5xl px-4 py-14 text-center sm:py-20">
            <div className="mb-5 flex justify-center gap-2">
              {[
                { code: 'fr' as const, label: 'FR' },
                { code: 'en' as const, label: 'EN' },
                { code: 'ar' as const, label: 'AR' },
              ].map((langOption) => {
                const isActive = activeLocale === langOption.code;
                return (
                  <button
                    key={langOption.code}
                    type="button"
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      if (langOption.code === 'fr') {
                        next.delete('lang');
                      } else {
                        next.set('lang', langOption.code);
                      }
                      setSearchParams(next, { replace: true });
                    }}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      isActive
                        ? 'border-white/70 bg-white text-slate-900'
                        : 'border-white/20 bg-white/5 text-slate-200 hover:border-white/40 hover:bg-white/10'
                    }`}
                    aria-pressed={isActive}
                  >
                    {langOption.label}
                  </button>
                );
              })}
            </div>
            <div className="mb-6 flex justify-center">
              <img
                src={logoZiyara}
                alt="Ziyara"
                className="h-24 w-auto drop-shadow-md sm:h-28 md:h-32"
              />
            </div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-200 shadow-lg shadow-amber-900/20">
              <Gift className="h-4 w-4 shrink-0 text-amber-300" aria-hidden />
              {copy.badge}
            </div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              {copy.heroTitle}
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
              {copy.heroSubtitleBeforeBold}
              <strong className="text-white">{copy.heroSubtitleBold}</strong>
              {copy.heroSubtitleAfterBold}
            </p>
          </div>
        </section>

        {/* Fonctionnalités — formulation simple, angle business */}
        <section className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <h2 className="text-center text-2xl font-bold text-white sm:text-3xl">
            {copy.pillarsTitle}
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {featuresInDisplayOrder().map((feature) => {
              const { id, icon: Icon, accent } = feature;
              const title = t(activeLocale, feature.title as WaitlistLocalized);
              const description = t(activeLocale, feature.description as WaitlistLocalized);
              const sitePreview =
                'agencySiteHostname' in feature && 'agencySiteName' in feature
                  ? { hostname: feature.agencySiteHostname, agencyName: feature.agencySiteName }
                  : undefined;
              return (
              <div
                key={id}
                className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] shadow-xl backdrop-blur-sm transition hover:border-white/20 hover:bg-white/[0.07]"
              >
                <div className="origin-top transition duration-300 group-hover:scale-[1.02]">
                  <FeatureVisual id={id} locale={activeLocale} sitePreview={sitePreview} />
                </div>
                <div className="p-6">
                  <div
                    className={`inline-flex rounded-xl bg-gradient-to-br ${accent} p-2.5 text-white shadow-lg`}
                  >
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-white">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{description}</p>
                </div>
              </div>
              );
            })}
          </div>
        </section>

        {/* Formulaire + offre */}
        <section className="mx-auto max-w-3xl px-4 pb-8">
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-slate-900/90 to-slate-950 shadow-2xl">
            <div className="border-b border-white/10 bg-indigo-600/30 px-6 py-4 text-center sm:px-8">
              <p className="text-sm font-medium text-indigo-100">
                {copy.offerBannerBeforeBold}
                <span className="font-bold text-white">{copy.offerBannerBold}</span>
                {copy.offerBannerAfterBold}
              </p>
            </div>

            <div className="p-6 sm:p-10">
              {status === 'success' ? (
                <div className="py-8 text-center">
                  <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-400" aria-hidden />
                  <h2 className="mt-6 text-2xl font-bold text-white">{copy.successTitle}</h2>
                  <p className="mt-3 text-slate-400">
                    {copy.successBodyBeforeBold}
                    <strong className="text-slate-200">{copy.successBodyBold}</strong>
                    {copy.successBodyAfterBold}
                  </p>
                  <Link
                    to="/home"
                    className="mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
                  >
                    {copy.backHome}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <h2 className="text-xl font-semibold text-white">{copy.formTitle}</h2>
                  <p className="text-sm text-slate-400">{copy.formIntro}</p>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="wl-firstName" className="block text-sm font-medium text-slate-300">
                        {copy.firstName}
                      </label>
                      <input
                        id="wl-firstName"
                        name="firstName"
                        type="text"
                        autoComplete="given-name"
                        value={form.firstName}
                        onChange={handleChange}
                        className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                        placeholder={copy.firstNamePlaceholder}
                      />
                    </div>
                    <div>
                      <label htmlFor="wl-lastName" className="block text-sm font-medium text-slate-300">
                        {copy.lastName}
                      </label>
                      <input
                        id="wl-lastName"
                        name="lastName"
                        type="text"
                        autoComplete="family-name"
                        value={form.lastName}
                        onChange={handleChange}
                        className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                        placeholder={copy.lastNamePlaceholder}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="wl-email" className="block text-sm font-medium text-slate-300">
                      {copy.email} <span className="text-rose-400">*</span>
                    </label>
                    <input
                      id="wl-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                      placeholder={copy.emailPlaceholder}
                    />
                  </div>

                  <div>
                    <label htmlFor="wl-agency" className="block text-sm font-medium text-slate-300">
                      {copy.agencyName}
                    </label>
                    <input
                      id="wl-agency"
                      name="agencyName"
                      type="text"
                      value={form.agencyName}
                      onChange={handleChange}
                      className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                      placeholder={copy.agencyNamePlaceholder}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="wl-rc" className="block text-sm font-medium text-slate-300">
                        {copy.rc}
                      </label>
                      <input
                        id="wl-rc"
                        name="commercialRegister"
                        type="text"
                        autoComplete="off"
                        value={form.commercialRegister}
                        onChange={handleChange}
                        className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                        placeholder={copy.rcPlaceholder}
                      />
                      <p className="mt-1 text-xs text-slate-500">{copy.rcHint}</p>
                    </div>
                    <div>
                      <label htmlFor="wl-wilaya" className="block text-sm font-medium text-slate-300">
                        {copy.wilaya}
                      </label>
                      <select
                        id="wl-wilaya"
                        name="wilaya"
                        value={form.wilaya}
                        onChange={handleChange}
                        className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 [&>option]:bg-slate-900 [&>option]:text-white"
                      >
                        <option value="">{copy.wilayaPlaceholder}</option>
                        {WILAYAS_DZ.map(({ code, name }) => (
                          <option key={code} value={code}>
                            {code} — {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="wl-phone" className="block text-sm font-medium text-slate-300">
                      {copy.phone}
                    </label>
                    <input
                      id="wl-phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={handleChange}
                      className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                      placeholder="+213 …"
                    />
                  </div>

                  <div>
                    <label htmlFor="wl-message" className="block text-sm font-medium text-slate-300">
                      {copy.message}
                    </label>
                    <textarea
                      id="wl-message"
                      name="message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      className="mt-1.5 w-full resize-y rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                      placeholder={copy.messagePlaceholder}
                    />
                  </div>

                  <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-400">
                    <input
                      type="checkbox"
                      name="acceptContact"
                      checked={form.acceptContact}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 rounded border-white/20 bg-white/10 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>
                      {copy.acceptContactBeforeBold}
                      <strong className="text-slate-200">{copy.acceptContactBold}</strong>
                      {copy.acceptContactAfterBold} <span className="text-rose-400">*</span>
                    </span>
                  </label>

                  {error ? (
                    <p className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-sm text-rose-200">
                      {error}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-4 text-base font-semibold text-white shadow-lg shadow-indigo-900/40 transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {copy.sending}
                      </>
                    ) : (
                      <>
                        {copy.submit}
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-slate-500">
                    {copy.privacy}
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
